import React, { ReactNode, useRef, useState } from 'react';
import styled from 'styled-components';
import { Autocomplete, Checkbox, Paper, PaperProps, TextField } from '@mui/material';
import { useAuth } from 'lib/Auth';
import { useDebounce } from 'hooks/useDebounce';
import { RecommendationSectionItem } from 'data/Product';
import { searchRecommendationProducts } from 'requests/productRecommendation';
import {
  RECOMMENDATION_SEARCH_DEBOUNCE_MS,
  RECOMMENDATION_SEARCH_MIN_LENGTH,
  RECOMMENDATION_STRINGS,
} from 'constants/productRecommendations';
import { Background, Border, Main, Text } from 'styles/themeColors';
import { BorderRadius } from 'styles/themeBorderRadius';

const Footer = styled.div`
  padding: 8px 16px;
  border-top: 1px solid ${Border.light};
  font-size: 13px;
  color: ${Text.placeholder};
`;

type PickerPaperProps = PaperProps & { footer?: ReactNode };

// Defined outside the picker so MUI doesn't remount the popup on every render.
const PickerPaper = ({ children, footer, ...rest }: PickerPaperProps) => (
  <Paper {...rest}>
    {children}
    {/* preventDefault keeps focus in the input, so clicking the footer doesn't close the popup */}
    <Footer onMouseDown={event => event.preventDefault()}>{footer}</Footer>
  </Paper>
);

interface Props {
  value: RecommendationSectionItem[];
  onChange: (items: RecommendationSectionItem[]) => void;
  vendorId?: number;
  excludeCode?: string;
}

/**
 * Multi-select search over the vendor's recommendable products. The value is the section's
 * own item list, so products already in the table come back ticked; ticking appends to the
 * end of the list, unticking removes.
 */
const RecommendationProductPicker = ({ value, onChange, vendorId, excludeCode }: Props) => {
  const { token } = useAuth();
  const [inputValue, setInputValue] = useState('');
  const [results, setResults] = useState<RecommendationSectionItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  // Only the latest request may update results; slower earlier responses are dropped.
  const requestSeq = useRef(0);

  const runSearch = useDebounce(async (search: string, seq: number) => {
    try {
      const products = await searchRecommendationProducts([search, vendorId, excludeCode], { token });
      if (seq !== requestSeq.current) return;
      setResults(products);
    } catch {
      if (seq !== requestSeq.current) return;
      setResults([]);
      setHasError(true);
    }
    setIsLoading(false);
  }, RECOMMENDATION_SEARCH_DEBOUNCE_MS);

  const onInputChange = (_: unknown, text: string, reason: string) => {
    // Only typing changes the search. MUI also rewrites the input on every tick and untick
    // (reasons 'selectOption' / 'removeOption' in v7, not just 'reset'); ignoring those keeps
    // the search so the vendor can tick more. Closing the dropdown clears it (clearSearch).
    if (reason !== 'input') return;

    setInputValue(text);
    const seq = ++requestSeq.current;
    setHasError(false);

    if (text.trim().length < RECOMMENDATION_SEARCH_MIN_LENGTH) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    runSearch(text.trim(), seq);
  };

  // 6639647 AC11: the search survives ticks while the dropdown is open, and is cleared on close.
  const clearSearch = () => {
    requestSeq.current++;
    setInputValue('');
    setResults([]);
    setIsLoading(false);
    setHasError(false);
  };

  const noOptionsText =inputValue.trim().length < RECOMMENDATION_SEARCH_MIN_LENGTH
    ? RECOMMENDATION_STRINGS.SEARCH_HINT
    : hasError ? RECOMMENDATION_STRINGS.SEARCH_FAILED : RECOMMENDATION_STRINGS.NO_PRODUCTS_FOUND;

  return (
    <Autocomplete
      id="recommendation-product-search"
      multiple
      disableCloseOnSelect
      // With chips hidden, MUI's clear button and Backspace-removes-last would delete table rows
      // with nothing visible to explain it. Removal happens by unticking or the row's delete icon.
      disableClearable
      onKeyDown={event => {
        if (event.key === 'Backspace' && !inputValue) {
          (event as React.KeyboardEvent & { defaultMuiPrevented?: boolean }).defaultMuiPrevented = true;
        }
      }}
      options={results}
      value={value}
      onChange={(_, items) => onChange(items)}
      inputValue={inputValue}
      onInputChange={onInputChange}
      onClose={clearSearch}
      filterOptions={options => options}
      getOptionLabel={option => option.name}
      isOptionEqualToValue={(option, selected) => option.recommendedProductCode === selected.recommendedProductCode}
      renderTags={() => null}
      loading={isLoading}
      loadingText={RECOMMENDATION_STRINGS.SEARCHING}
      noOptionsText={noOptionsText}
      slots={{ paper: PickerPaper }}
      slotProps={{ paper: { footer: RECOMMENDATION_STRINGS.PRODUCTS_SELECTED(value.length) } as PickerPaperProps }}
      renderOption={(props, option, { selected }) => {
        const { key, ...optionProps } = props as typeof props & { key: string };
        return (
          <li key={option.recommendedProductCode} {...optionProps}>
            <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0, '&.Mui-checked': { color: Main.accent } }} />
            {option.name}
          </li>
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={RECOMMENDATION_STRINGS.MANUAL_ADD_PLACEHOLDER}
          variant="outlined"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: BorderRadius.m,
              backgroundColor: Background.white,
              '& fieldset': { borderColor: Border.main },
              '&.Mui-focused fieldset': { borderColor: Main.accent },
            },
          }}
        />
      )}
    />
  );
};

export default RecommendationProductPicker;
